import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefundAmountInformationBox from "../../components/common/RefundAmountInformationBox";
import FeeWaiverAmountDetails from "../../components/common/FeeWaiverAmountDetails";
import apiService from "../../services/api";
import CustomLoading from "../../components/common/CustomLoading";
import {
  formatMobileNumber,
  formatTimestampAsDateTime,
} from "../../utils/formatters";
import { parsePaymentSummaryForPlanVehicle } from "../../utils/paymentSummaryParse";
import { hasPaymentRefundableAmount } from "../../utils/paymentRefundEligibility";
import {
  defaultRefundAmountInput,
  formatRefundAmountDisplay,
  formatRefundCurrency,
  parseRefundAmountInput,
  resolvePaymentRefundDialogAmounts,
} from "../../utils/paymentRefundDialogAmounts";
import { useNotification } from "../../contexts/NotificationContext";
import { DATE_FORMATS } from "../../constants";

function isTruthyRefundAddedFlag(v: unknown): boolean {
  return (
    v === true ||
    v === 1 ||
    (typeof v === "string" && v.trim().toLowerCase() === "true")
  );
}
function isPaymentRefundAlreadyAdded(p: Record<string, unknown>): boolean {
  return (
    isTruthyRefundAddedFlag(p.isrefundadded) ||
    isTruthyRefundAddedFlag(p.isRefundAdded) ||
    isTruthyRefundAddedFlag(p.IsRefundAdded)
  );
}

function pickPaymentAmount(
  payment: Record<string, unknown>,
  keys: string[],
): number {
  for (const key of keys) {
    const v = payment[key];
    if (v == null || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function pickPaymentAmountOptional(
  payment: Record<string, unknown>,
  keys: string[],
): number | undefined {
  const n = pickPaymentAmount(payment, keys);
  return n > 0 ? n : undefined;
}

function formatPaymentBreakdownAmount(value: number): string {
  const n = Number.isFinite(value) ? value : 0;
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

type PaymentBreakdownRow = {
  label: string;
  value: string;
  amount: number;
  previousAmount?: number;
};

const ONBOARDING_PROCESSING_LABEL = "Processing Fees";
const ONBOARDING_DEPOSIT_LABEL = "Deposit Amount";
const CASH_HANDLING_LABEL = "Cash Handling Fees";

/** Match create-booking PlanStep cost breakdown typography. */
const paymentBreakdownLabelSx = (options?: {
  isDiscount?: boolean;
  isTotal?: boolean;
}) => ({
  fontWeight: options?.isTotal ? 700 : options?.isDiscount ? 700 : 600,
  color: options?.isDiscount ? "success.main" : "text.primary",
});

const paymentBreakdownValueSx = (options?: {
  isDiscount?: boolean;
  isTotal?: boolean;
}) => ({
  fontWeight: options?.isTotal ? 700 : 600,
  color: options?.isDiscount ? "success.main" : "text.primary",
});

function shouldShowOnboardingPreviousRow(
  previous: number,
  current: number,
): boolean {
  return previous > 0 && previous != current;
}

function pushBreakdownRow(
  rows: PaymentBreakdownRow[],
  label: string,
  amount: number,
  options?: { hideWhenZero?: boolean; previousAmount?: number },
): void {
  if (options?.hideWhenZero && amount <= 0) return;
  rows.push({
    label,
    value: formatPaymentBreakdownAmount(amount),
    amount,
    previousAmount: options?.previousAmount,
  });
}

function pickWaiverPaymentRecord(
  payment: Record<string, unknown>,
): Record<string, unknown> | null {
  const list = payment.waiverpaymentlist ?? payment.waiverPaymentList;
  if (!Array.isArray(list) || list.length === 0) return null;

  const bookingPlanId = pickPaymentAmount(payment, [
    "bookingplanid",
    "bookingPlanId",
  ]);

  if (bookingPlanId > 0) {
    for (const item of list) {
      if (item == null || typeof item !== "object") continue;
      const rec = item as Record<string, unknown>;
      const planId = pickPaymentAmount(rec, ["bookingplanid", "bookingPlanId"]);
      if (planId === bookingPlanId) return rec;
    }
  }

  const first = list[0];
  return first != null && typeof first === "object"
    ? (first as Record<string, unknown>)
    : null;
}

/** waiver* = edited amount when changed; 0 when unchanged (same as create-booking API). */
function resolveWaiverBreakdownAmounts(
  systemAmount: number,
  waiverAmount: number,
  paymentAmount: number,
  fallbackPrevious?: number,
): { current: number; previous?: number } {
  const current = waiverAmount > 0 ? waiverAmount : paymentAmount;
  const previousValue = Math.max(
    systemAmount > 0 ? systemAmount : 0,
    fallbackPrevious != null && fallbackPrevious > 0 ? fallbackPrevious : 0,
  );
  const previous = previousValue > 0 ? previousValue : undefined;
  return { current, previous };
}

function buildPaymentDetailsBreakdown(payment: Record<string, unknown>): {
  rows: PaymentBreakdownRow[];
} {
  const waiverRecord = pickWaiverPaymentRecord(payment);

  const planPrice = pickPaymentAmount(payment, [
    "price",
    "planprice",
    "planPrice",
  ]);
  const paymentProcessingFees = pickPaymentAmount(payment, [
    "processingfees",
    "processingFees",
    "processing_fees",
  ]);
  const paymentCashHandlingFees = pickPaymentAmount(payment, [
    "cashpaymentchargeamount",
    "cashPaymentChargeAmount",
    "cashpaymentcharge",
    "cashPaymentCharge",
  ]);
  const paymentDepositAmount = pickPaymentAmount(payment, [
    "refundableamount",
    "refundableAmount",
  ]);

  const previousProcessingFromPayment = pickPaymentAmountOptional(payment, [
    "originalprocessingfees",
    "originalProcessingFees",
    "planprocessingfees",
    "planProcessingFees",
  ]);
  const previousDepositFromPayment = pickPaymentAmountOptional(payment, [
    "originalrefundableamount",
    "originalRefundableAmount",
  ]);
  const previousCashFromPayment = pickPaymentAmountOptional(payment, [
    "originalcashpaymentchargeamount",
    "originalCashPaymentChargeAmount",
  ]);

  const processingFromWaiver = waiverRecord
    ? resolveWaiverBreakdownAmounts(
      pickPaymentAmount(waiverRecord, [
        "systemprocessingfees",
        "systemProcessingFees",
      ]),
      pickPaymentAmount(waiverRecord, [
        "waiverprocessingfees",
        "waiverProcessingFees",
      ]),
      paymentProcessingFees,
      previousProcessingFromPayment,
    )
    : {
      current: paymentProcessingFees,
      previous: previousProcessingFromPayment,
    };

  const depositFromWaiver = waiverRecord
    ? resolveWaiverBreakdownAmounts(
      pickPaymentAmount(waiverRecord, [
        "systemsecuritydeposite",
        "systemSecurityDeposite",
        "systemsecuritydeposit",
        "systemSecurityDeposit",
      ]),
      pickPaymentAmount(waiverRecord, [
        "waiversecuritydeposite",
        "waiverSecurityDeposite",
        "waiversecuritydeposit",
        "waiverSecurityDeposit",
      ]),
      paymentDepositAmount,
      previousDepositFromPayment,
    )
    : {
      current: paymentDepositAmount,
      previous: previousDepositFromPayment,
    };

  const cashFromWaiver = waiverRecord
    ? resolveWaiverBreakdownAmounts(
      pickPaymentAmount(waiverRecord, [
        "systemcashhandlingfees",
        "systemCashHandlingFees",
      ]),
      pickPaymentAmount(waiverRecord, [
        "waivercashhandlingfees",
        "waiverCashHandlingFees",
      ]),
      paymentCashHandlingFees,
      previousCashFromPayment,
    )
    : {
      current: paymentCashHandlingFees,
      previous: previousCashFromPayment,
    };

  const processingFees = processingFromWaiver.current;
  const previousProcessingFees = processingFromWaiver.previous;
  const cashHandlingFees = cashFromWaiver.current;
  const previousCashHandlingFees = cashFromWaiver.previous;
  const depositAmount = depositFromWaiver.current;
  const previousDepositAmount = depositFromWaiver.previous;
  const lateReturnFees = pickPaymentAmount(payment, [
    "latereturnfees",
    "lateReturnFees",
    "latefees",
    "lateFees",
  ]);
  const bookingAmount = pickPaymentAmount(payment, [
    "bookingamount",
    "bookingAmount",
  ]);
  const discount = pickPaymentAmount(payment, [
    "discountedamount",
    "discountedAmount",
  ]);
  const totalPayment = pickPaymentAmount(payment, [
    "amount",
    "totalpayment",
    "totalPayment",
    "totalpayableamount",
  ]);

  const refundAlreadyAdded = isPaymentRefundAlreadyAdded(payment);
  const amountRefunded = refundAlreadyAdded
    ? pickPaymentAmount(payment, [
      "refundedamount",
      "refundedAmount",
      "amountrefunded",
      "amountRefunded",
      "finalrefundamount",
    ]) || depositAmount
    : 0;

  const rows: PaymentBreakdownRow[] = [];
  pushBreakdownRow(rows, "Plan Price", planPrice);
  pushBreakdownRow(rows, ONBOARDING_PROCESSING_LABEL, processingFees, {
    previousAmount: previousProcessingFees,
  });
  pushBreakdownRow(rows, ONBOARDING_DEPOSIT_LABEL, depositAmount, {
    previousAmount: previousDepositAmount,
  });
  pushBreakdownRow(rows, CASH_HANDLING_LABEL, cashHandlingFees, {
    previousAmount: previousCashHandlingFees,
  });

  pushBreakdownRow(rows, "Late Return Fees", lateReturnFees, {
    hideWhenZero: true,
  });

  pushBreakdownRow(rows, "Discount", discount, { hideWhenZero: true });
  pushBreakdownRow(rows, "Total Amount", totalPayment);
  // if (refundAlreadyAdded) {
  //   pushBreakdownRow(rows, "Amount Refunded", amountRefunded);
  // }

  return { rows };
}

const PaymentDetails: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { id } = useParams<{ id: string }>();
  const [paymentData, setPaymentData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundRemark, setRefundRemark] = useState("");
  const [refundAmountInput, setRefundAmountInput] = useState("");
  const [refundAmountError, setRefundAmountError] = useState("");
  const [refundDescription, setRefundDescription] = useState("");
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);
  const [refundSuccess, setRefundSuccess] = useState<string | null>(null);
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  useEffect(() => {
    if (id) {
      fetchPaymentDetails(id);
    }
  }, [id]);

  const fetchPaymentDetails = async (paymentId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getPaymentDetails(paymentId);
      const paymentDetails = (response as any)?.data || response;
      setPaymentData(paymentDetails);
    } catch (error: any) {
      setError("Failed to load payment details. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const issueCouponHeaderBlue = "rgba(22, 67, 135, 1)";
  const issueCouponSubmitYellow = "#FFEB3B";
  const issueCouponPrimaryButtonSx = {
    backgroundColor: issueCouponSubmitYellow,
    color: issueCouponHeaderBlue,
    fontWeight: 700,
    textTransform: "none" as const,
    px: 2.5,
    py: 1,
    minHeight: 40,
    borderRadius: 1,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#FDD835",
      boxShadow: "none",
    },
    "&:disabled": {
      backgroundColor: "rgba(255, 235, 59, 0.5)",
      color: "rgba(22, 67, 135, 0.6)",
    },
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return "N/A";
    const formatted = DATE_FORMATS.formatDateTime(dateString)
    return formatted || dateString;
  };

  const formatDiscountedAmount = (raw: unknown): string => {
    if (raw == null || raw === "") return "";
    const numericRaw =
      typeof raw === "number" || typeof raw === "string" ? raw : "";
    const n =
      typeof numericRaw === "number"
        ? numericRaw
        : Number.parseFloat(numericRaw);
    if (!Number.isFinite(n)) return "";
    return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPlanVehicle = (summaryText: string) => {
    if (!summaryText || summaryText === "N/A") {
      return { plan: "N/A", vehicle: "N/A" };
    }
    const { plan, vehicle } = parsePaymentSummaryForPlanVehicle(summaryText);
    return {
      plan: plan || "N/A",
      vehicle: vehicle || "N/A",
    };
  };

  const toNumber = (value: unknown): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const paymentBreakdown = useMemo(() => {
    if (!paymentData || typeof paymentData !== "object") {
      return { rows: [] };
    }
    return buildPaymentDetailsBreakdown(paymentData as Record<string, unknown>);
  }, [paymentData]);

  const refundDialogAmounts = useMemo(() => {
    if (!paymentData || typeof paymentData !== "object") {
      return { total: 0 };
    }
    return resolvePaymentRefundDialogAmounts(
      paymentData as Record<string, unknown>,
    );
  }, [paymentData]);

  const validateRefundAmountInput = (
    value: string,
    paymentTotal: number,
  ): string => {
    const trimmed = value.trim();
    if (trimmed === "") return "";
    const amount = parseRefundAmountInput(trimmed);
    if (amount == null) return "Please enter a valid refundable amount.";
    if (amount < 0) return "Amount cannot be negative.";
    if (paymentTotal > 0 && amount > paymentTotal) {
      return `Refundable amount cannot be greater than total amount (${formatRefundCurrency(paymentTotal)}).`;
    }
    return "";
  };

  const handleRefundAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRefundAmountInput(value);
    setRefundAmountError(
      validateRefundAmountInput(value, refundDialogAmounts.total),
    );
    if (refundError) setRefundError(null);
  };

  const openRefundDialog = () => {
    const fallbackDescription =
      typeof paymentData?.summeryofpayments === "string"
        ? paymentData.summeryofpayments
        : "";
    const defaultAmount = defaultRefundAmountInput([
      { amount: paymentData?.amount ?? 0, refundableamount: null },
    ]);
    setRefundDescription(fallbackDescription);
    setRefundRemark("");
    setRefundAmountInput(defaultAmount);
    setRefundAmountError(
      validateRefundAmountInput(defaultAmount, refundDialogAmounts.total),
    );
    setRefundError(null);
    setRefundSuccess(null);
    setRefundDialogOpen(true);
  };

  const closeRefundDialog = () => {
    if (refundSubmitting) return;
    setRefundDialogOpen(false);
    setRefundError(null);
    setRefundAmountError("");
  };

  const handleProcessRefund = async () => {
    const bookingmasterid = toNumber(
      paymentData?.bookingmasterid ?? paymentData?.bookingid,
    );
    const bookingplanid = toNumber(
      paymentData?.bookingplanid ??
      paymentData?.planid ??
      paymentData?.bookingPlanId,
    );
    const remark = refundRemark.trim();
    const amount = parseRefundAmountInput(refundAmountInput);
    const { total: paymentTotal } = refundDialogAmounts;

    if (bookingmasterid <= 0) {
      setRefundError("Booking ID is missing in payment details.");
      return;
    }
    if (remark === "") {
      setRefundError("Please enter a remark.");
      return;
    }
    if (amount == null || amount <= 0) {
      setRefundError("Please enter a valid proposed refund amount.");
      return;
    }
    const amountValidationError = validateRefundAmountInput(
      refundAmountInput,
      paymentTotal,
    );
    if (amountValidationError) {
      setRefundAmountError(amountValidationError);
      setRefundError(amountValidationError);
      return;
    }

    setRefundSubmitting(true);
    setRefundError(null);
    setRefundSuccess(null);
    try {
      const res = (await apiService.addRefund([
        {
          bookingmasterid,
          bookingplanid,
          amount,
          refunddescription: remark,
          refundType: "0",
          isaddedbyweb: true,
          paymentid: toNumber(paymentData?.paymentid ?? paymentData?.paymentId),
        },
      ])) as { isSuccess?: boolean; message?: string };

      if (res && typeof res === "object" && res.isSuccess === false) {
        throw new Error(
          typeof res.message === "string" && res.message.trim() !== ""
            ? res.message
            : "Refund request failed.",
        );
      }

      const msg =
        typeof res?.message === "string" && res.message.trim() !== ""
          ? res.message.trim()
          : "Refund request submitted successfully.";

      setRefundDialogOpen(false);
      showNotification(msg, "success");
      navigate("/payment-management");
    } catch (e: unknown) {
      const msg =
        e instanceof Error && e.message.trim() !== ""
          ? e.message
          : "Failed to process refund.";

      showNotification(msg, "error");
    } finally {
      setRefundSubmitting(false);
    }
  };

  if (loading) {
    return <CustomLoading message="Fetching Payment Details..." />;
  }

  if (error) {
    return (
      <Container fluid>
        <Alert severity="error" className="mb-3">
          {error}
        </Alert>
        <Button variant="outlined" onClick={onBack}>
          Go Back
        </Button>
      </Container>
    );
  }

  if (!paymentData) {
    return (
      <Container fluid>
        <Alert severity="warning" className="mb-3">
          No payment data found.
        </Alert>
        <Button variant="outlined" onClick={onBack}>
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="pb-5">
      {/* Basic Information Section */}
      <Paper className="mb-4 shadow-sm border-0 rounded-3">
        <Box className="px-4 pt-4 pb-4">
          {/* Header with Status */}
          <Box className="d-flex justify-content-between align-items-center mb-3">
            <Typography
              variant="h6"
              className="fw-bold mb-0"
              sx={{ color: "rgba(13,44,90,1)", fontSize: "1.1rem" }}
            >
              Payment Information
            </Typography>
          </Box>

          {/* Divider Line */}
          <Box
            sx={{
              height: "1px",
              backgroundColor: "#e9ecef",
              mb: 3,
            }}
          />

          {/* Basic Information Grid */}
          <Row className="g-4">
            {[
              { label: "Payment ID", value: paymentData.paymentid || "N/A" },
              { label: "Booking ID", value: paymentData.bookingid || "N/A" },
              { label: "User Name", value: paymentData.username || "N/A" },
              {
                label: "Mobile Number",
                value: formatMobileNumber(paymentData.mobileno) || "N/A",
              },
              {
                label: "Total Amount",
                value: paymentData.amount ? `₹${paymentData.amount}` : "N/A",
              },
              {
                label: "Discounted Amount",
                value: formatDiscountedAmount(
                  paymentData.discountedamount ?? paymentData.discountedAmount,
                ),
              },
            ].map((field, idx) => (
              <Col xs={12} sm={6} md={4} key={idx}>
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#6c757d",
                      mb: 0.5,
                      fontSize: "0.85rem",
                      fontWeight: 500,
                    }}
                  >
                    {field.label}
                  </Typography>
                  <Typography
                    variant="body1"
                    className="fw-bold"
                    sx={{
                      color: "#495057",
                      fontSize: "0.95rem",
                      wordBreak: "break-word",
                    }}
                  >
                    {field.value}
                  </Typography>
                </Box>
              </Col>
            ))}
          </Row>
        </Box>
      </Paper>

      {/* Payment Summary Section */}
      <Paper className="mb-4 shadow-sm border-0 rounded-3">
        <Box className="px-4 pt-4 pb-4">
          {/* Header */}
          <Typography
            variant="h6"
            className="fw-bold mb-3"
            sx={{ color: "rgba(13,44,90,1)", fontSize: "1.1rem" }}
          >
            Payment Summary
          </Typography>

          {/* Divider Line */}
          <Box
            sx={{
              height: "1px",
              backgroundColor: "#e9ecef",
              mb: 3,
            }}
          />

          {/* Payment Summary Information Grid */}
          <Row className="g-4">
            {(() => {
              const { plan, vehicle } = formatPlanVehicle(
                paymentData.summeryofpayments,
              );
              return [
                { label: "Plan", value: plan },
                { label: "Vehicle", value: vehicle },
                { label: "Hub Name", value: paymentData.hubname || "N/A" },
                {
                  label: "Transaction ID",
                  value: paymentData.transactionid.includes("cash")
                    ? "Cash"
                    : paymentData.transactionid || "N/A",
                },
                {
                  label: "Payment Date",
                  value: formatDateTime(paymentData.enteredon),
                },
              ].map((field, idx) => (
                <Col xs={12} sm={6} md={idx === 4 ? 12 : 3} key={idx}>
                  <Box sx={{ textAlign: "left" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6c757d",
                        mb: 0.5,
                        fontSize: "0.85rem",
                        fontWeight: 500,
                      }}
                    >
                      {field.label}
                    </Typography>
                    <Typography
                      variant="body1"
                      className="fw-bold"
                      sx={{
                        color: "#495057",
                        fontSize: "0.95rem",
                        wordBreak: "break-word",
                      }}
                    >
                      {field.value}
                    </Typography>
                  </Box>
                </Col>
              ));
            })()}
          </Row>
        </Box>
      </Paper>

      <Paper
        className="mb-4 shadow-sm border-0 rounded-3"
        sx={{ border: "1px solid #e0e0e0" }}
      >
        <Box className="px-4 pt-4 pb-4">
          <Typography
            variant="h6"
            className="fw-bold mb-1"
            sx={{ color: "rgba(13,44,90,1)", fontSize: "1.1rem" }}
          >
            Payment Breakdown
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            Amount details from this payment record
          </Typography>
          <Box
            sx={{
              borderRadius: 1,
              border: "1px solid #e9ecef",
              bgcolor: "#fafafa",
              overflow: "hidden",
            }}
          >
            {paymentBreakdown.rows.map((row, index) => {
              const isTotalRow = row.label === "Total Amount";
              const isDiscountRow = row.label === "Discount";
              const rowBorderBottom =
                index < paymentBreakdown.rows.length - 1
                  ? "1px solid #eee"
                  : "none";
              const showOnboardingPreviousAccordion =
                row.previousAmount != null &&
                shouldShowOnboardingPreviousRow(row.previousAmount, row.amount);

              if (showOnboardingPreviousAccordion) {
                return (
                  <Accordion
                    key={row.label}
                    disableGutters
                    elevation={0}
                    sx={{
                      borderBottom: rowBorderBottom,
                      bgcolor: "transparent",
                      px: 2,
                      "&::before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
                      sx={{
                        px: 0,
                        minHeight: 48,
                        "& .MuiAccordionSummary-content": {
                          my: 0.75,
                          alignItems: "center",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "baseline",
                          gap: 2,
                          width: "100%",
                          pr: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={paymentBreakdownLabelSx({
                            isDiscount: isDiscountRow,
                          })}
                        >
                          {row.label}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={paymentBreakdownValueSx({
                            isDiscount: isDiscountRow,
                          })}
                        >
                          {row.value}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, pt: 0, pb: 1.5 }}>
                      <FeeWaiverAmountDetails
                        previousAmount={row.previousAmount!}
                        currentAmount={row.amount}
                        formatAmount={formatPaymentBreakdownAmount}
                      />
                    </AccordionDetails>
                  </Accordion>
                );
              }

              return (
                <Box
                  key={row.label}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 2,
                    px: 2,
                    py: 1.5,
                    borderBottom: rowBorderBottom,
                    ...(isTotalRow
                      ? {
                        bgcolor: "rgba(227, 242, 253, 0.35)",
                        borderTop: "1px solid rgba(144, 202, 249, 0.45)",
                      }
                      : {}),
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={paymentBreakdownLabelSx({
                      isDiscount: isDiscountRow,
                      isTotal: isTotalRow,
                    })}
                  >
                    {row.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={paymentBreakdownValueSx({
                      isDiscount: isDiscountRow,
                      isTotal: isTotalRow,
                    })}
                  >
                    {row.value}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Paper>

      {/* Action Buttons — refund only for booking-related payment summaries, and not if refund already recorded */}
      {String(
        paymentData.summeryofpayments ?? paymentData.summeryOfPayments ?? "",
      )
        .toLowerCase()
        .includes("booking") &&
        !isPaymentRefundAlreadyAdded(paymentData) &&
        hasPaymentRefundableAmount(paymentData) ? (
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: { xs: "stretch", md: "flex-end" },
          }}
        >
          <Button
            variant="contained"
            onClick={openRefundDialog}
            sx={{
              backgroundColor: "rgba(22, 67, 135, 1)",
              textTransform: "none",
              fontWeight: 600,
              minWidth: { xs: "100%", md: 180 },
              "&:hover": { backgroundColor: "rgba(18, 57, 116, 1)" },
            }}
            style={{
              marginRight: "10px",
            }}
          >
            Process Refund
          </Button>
          <Button
            onClick={() =>
              navigate("/payment-management", {
                state: {
                  filters: { paymentstatus: 0 },
                  page: 0,
                  shouldRefetch: true,
                },
              })
            }
            sx={{
              color: issueCouponHeaderBlue,
              fontWeight: 600,
              textTransform: "none",
              border: "1px solid rgba(22, 67, 135, 0.6)",
            }}
          >
            cancel
          </Button>
        </Box>
      ) : null}

      <Dialog
        open={refundDialogOpen}
        onClose={closeRefundDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            m: 0,
            py: 1.75,
            px: 2,
            backgroundColor: issueCouponHeaderBlue,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: 700,
            fontSize: "1.125rem",
          }}
        >
          Process Refund
          <IconButton
            type="button"
            onClick={closeRefundDialog}
            disabled={refundSubmitting}
            size="small"
            aria-label="Close"
            sx={{
              color: "#fff !important",
              bgcolor: "rgba(255,255,255,0.2)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            px: 2.5,
            pb: 2,
            pt: 3,
            position: "relative",
          }}
        >
          {refundError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {refundError}
            </Alert>
          ) : null}

          <Box mt={refundError ? 0 : 2}>
            <RefundAmountInformationBox
              rows={[
                {
                  label: "Total Amount",
                  value: formatRefundAmountDisplay(refundDialogAmounts.total),
                },
              ]}
            />
          </Box>
          <Box mt={2}>
            <TextField
              fullWidth
              required
              label="Refundable Amount"
              type="number"
              size="small"
              value={refundAmountInput}
              onChange={handleRefundAmountChange}
              inputProps={{
                min: 0,
                step: "0.01",
                max:
                  refundDialogAmounts.total > 0
                    ? refundDialogAmounts.total
                    : undefined,
              }}
              disabled={refundSubmitting}
              error={!!refundAmountError}
              helperText={refundAmountError || undefined}
            />
          </Box>
          <Box mt={2}>
            <TextField
              fullWidth
              required
              label="Remark"
              size="small"
              value={refundRemark}
              onChange={(e) => setRefundRemark(e.target.value)}
              disabled={refundSubmitting}
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            pb: 2,
            pt: 1,
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            onClick={closeRefundDialog}
            disabled={refundSubmitting}
            sx={{
              color: issueCouponHeaderBlue,
              fontWeight: 600,
              textTransform: "none",
              border: "1px solid rgba(22, 67, 135, 0.6)",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleProcessRefund()}
            disabled={refundSubmitting || !!refundAmountError}
            sx={issueCouponPrimaryButtonSx}
          >
            {refundSubmitting ? "Submitting..." : "Submit Refund"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PaymentDetails;
